// context/DataContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from './i18n/config';

// Create context
const DataContext = createContext();

const fetchBlogs = async (languageCode) => {
    const url = languageCode
        ? `${process.env.REACT_APP_BLOGS_ENDPOINT}?languageCode=${encodeURIComponent(languageCode)}`
        : `${process.env.REACT_APP_BLOGS_ENDPOINT}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch blogs');
    return response.json();
};

export const DataProvider = ({ children }) => {
    const [blogs, setBlogs] = useState([]);

    const [isLoadingBlogs, setIsLoadingBlogs] = useState(true);

    const [error, setError] = useState(null);

    const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

    useEffect(() => {
        const handleLanguageChange = (lng) => {
            setCurrentLanguage(lng);
        };
        i18n.on('languageChanged', handleLanguageChange);
        return () => i18n.off('languageChanged', handleLanguageChange);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoadingBlogs(true);

                // Use Promise.all to fetch both concurrently
                const [blogsData] = await Promise.all([fetchBlogs(currentLanguage)]);

                setBlogs(blogsData);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoadingBlogs(false);
            }
        };

        fetchData();
    }, [currentLanguage]);

    return (
        <DataContext.Provider value={{ blogs, isLoadingBlogs, error }}>
            {children}
        </DataContext.Provider>
    );
};

// Custom hook to use context
export const useData = () => useContext(DataContext);
